import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import { capitalizeFirst, getStatusColor } from '../../utils';
import {
  MessageCircle,
  AlertCircle,
  CheckCheck,
  Clock,
  Filter,
} from 'lucide-react';
import { ADMIN_SUPPORT_FILTER_FIELDS } from '../../constants/filters';
import { supportTicketAPI } from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupportTicketModal from '../../components/admin/SupportTicketModal';
import { SupportTicket } from '../../utils/types';

interface FilterField {
  id: string;
  label: string;
  options: { value: string; label: string }[];
}

const AdminSupport = () => {
  const [activeTab, setActiveTab] = useState<
    'all' | 'Open' | 'In-progress' | 'Resolved'
  >('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    ticket: SupportTicket | null;
    isOpen: boolean;
  }>({
    ticket: null,
    isOpen: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination] = useState({
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await supportTicketAPI.getAdminTickets();
        if (response.success) {
          setTickets(response.data?.tickets || []);
        } else {
          console.error('Failed to fetch tickets:', response.error);
          toast.error(response.error || 'Failed to fetch tickets');
        }
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Error fetching support tickets'
        );
      }
    };
    fetchTickets();
  }, [pagination]);

  const filterFields: FilterField[] = ADMIN_SUPPORT_FILTER_FIELDS;

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const handleReplySubmit = async (
    ticketId: number,
    reply: string,
    status: string
  ) => {
    setIsSubmitting(true);
    try {
      const response = await supportTicketAPI.updateTicket(ticketId, {
        status: status as 'Open' | 'In-progress' | 'Resolved' | 'Closed',
        message: reply,
      });

      if (response.success) {
        toast.success(response.message || 'Ticket updated successfully');
        const refreshResponse = await supportTicketAPI.getAdminTickets();
        if (refreshResponse.success) {
          setTickets(refreshResponse.data?.tickets || []);
        }
        handleModalClose();
      } else {
        console.error('Failed to update ticket:', response.error);
        if (
          response.message?.includes('Cannot edit ticket') ||
          response.message?.includes('already resolved') ||
          response.message?.includes('cannot be modified')
        ) {
          toast.error(
            response.message || 'Cannot edit resolved or closed tickets'
          );
          handleModalClose();
        } else {
          toast.error(response.error || 'Failed to update ticket');
        }
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error updating support ticket';

      if (
        errorMessage?.includes('Cannot edit ticket') ||
        errorMessage?.includes('already resolved') ||
        errorMessage?.includes('cannot be modified')
      ) {
        toast.error(errorMessage);
        handleModalClose();
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTicketView = (ticket: SupportTicket, isViewOnly = false) => {
    if (!isViewOnly && ticket.status === 'Resolved') {
      toast.error('Cannot edit resolved tickets');
      return;
    }

    setSelectedTicket(ticket);
    setViewMode(isViewOnly);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedTicket(null);
    setIsModalOpen(false);
    setViewMode(false);
  };

  const handleViewModeChange = (newViewMode: boolean) => {
    setViewMode(newViewMode);
  };

  const handleDeleteClick = (ticket: SupportTicket) => {
    setDeleteConfirm({ ticket, isOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.ticket) return;

    setIsDeleting(true);
    try {
      const response = await supportTicketAPI.adminDeleteTicket(
        deleteConfirm.ticket.id.toString()
      );

      if (response.success) {
        toast.success(response.message || 'Ticket deleted successfully');
        setDeleteConfirm({ ticket: null, isOpen: false });
        const refreshResponse = await supportTicketAPI.getAdminTickets();
        if (refreshResponse.success) {
          setTickets(refreshResponse.data?.tickets || []);
        }
      } else {
        toast.error(response.message || 'Failed to delete ticket');
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      let errorMessage = 'Error deleting support ticket';

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ ticket: null, isOpen: false });
  };

  const filteredTickets = Array.isArray(tickets)
    ? tickets.filter(ticket => {
        const isNotDeleted = ticket.is_deleted !== true;

        const matchesSearch =
          searchTerm === '' ||
          ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.creator?.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          ticket.ticketId.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesTab = activeTab === 'all' || ticket.status === activeTab;

        const matchesFilters = Object.keys(activeFilters).every(key => {
          const value = activeFilters[key];
          return !value || (ticket as any)[key] === value;
        });

        return isNotDeleted && matchesSearch && matchesTab && matchesFilters;
      })
    : [];

  const stats = {
    total: Array.isArray(tickets)
      ? tickets.filter(t => t.is_deleted !== true).length
      : 0,
    open: Array.isArray(tickets)
      ? tickets.filter(t => t.status === 'Open' && t.is_deleted !== true).length
      : 0,
    inProgress: Array.isArray(tickets)
      ? tickets.filter(t => t.status === 'In-progress' && t.is_deleted !== true)
          .length
      : 0,
    resolved: Array.isArray(tickets)
      ? tickets.filter(t => t.status === 'Resolved' && t.is_deleted !== true)
          .length
      : 0,
    urgent: Array.isArray(tickets)
      ? tickets.filter(
          t =>
            t.priority === 'urgent' &&
            t.status !== 'Closed' &&
            t.is_deleted !== true
        ).length
      : 0,
  };

  const columns = [
    {
      id: 'ticketId',
      header: 'Ticket ID',
      accessor: (row: SupportTicket) => row.ticketId,
      sortable: true,
      sortValue: (row: SupportTicket) => row.ticketId,
      width: '150px',
    },
    {
      id: 'userName',
      header: 'User',
      accessor: (row: SupportTicket) => row.creator?.fullName || '',
      sortable: true,
      sortValue: (row: SupportTicket) => row.creator?.fullName || '',
      width: '140px',
    },
    {
      id: 'userEmail',
      header: 'Email',
      accessor: (row: SupportTicket) => row.creator?.email || '',
      sortable: true,
      sortValue: (row: SupportTicket) => row.creator?.email || '',
      width: '180px',
    },
    {
      id: 'subject',
      header: 'Subject',
      accessor: (row: SupportTicket) => row.subject,
      sortable: true,
      sortValue: (row: SupportTicket) => row.subject,
      width: '200px',
    },
    {
      id: 'category',
      header: 'Category',
      accessor: (row: SupportTicket) => (
        <Badge className={getStatusColor(row.category)}>
          {capitalizeFirst(row.category)}
        </Badge>
      ),
      sortable: true,
      sortValue: (row: SupportTicket) => row.category,
      width: '100px',
    },
    {
      id: 'priority',
      header: 'Priority',
      accessor: (row: SupportTicket) => (
        <Badge className={getStatusColor(row.priority, 'priority')}>
          {capitalizeFirst(row.priority)}
        </Badge>
      ),
      sortable: true,
      sortValue: (row: SupportTicket) => row.priority,
      width: '90px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: SupportTicket) => (
        <Badge className={getStatusColor(row.status)}>
          {capitalizeFirst(row.status)}
        </Badge>
      ),
      sortable: true,
      sortValue: (row: SupportTicket) => row.status,
      width: '100px',
    },
    {
      id: 'assigned',
      header: 'Assigned To',
      accessor: (row: SupportTicket) =>
        row.assignedUser?.fullName || 'Super Admin',
      sortable: true,
      sortValue: (row: SupportTicket) =>
        row.assignedUser?.fullName || 'Super   Admin',
      width: '120px',
    },
    {
      id: 'created',
      header: 'Created',
      accessor: (row: SupportTicket) =>
        moment(row.createdAt).format('MMM DD, YYYY hh:mm A'),
      sortable: true,
      sortValue: (row: SupportTicket) => row.createdAt,
      width: '180px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: (row: SupportTicket) => (
        <ActionDropdown
          id={`ticket-${row.id}`}
          size="icon"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={row.status !== 'Resolved' && row.status !== 'Closed'}
          onView={() => handleTicketView(row, true)}
          onDelete={() => handleDeleteClick(row)}
        />
      ),
      width: '100px',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Support Management"
          description="Manage and respond to customer support tickets"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tickets</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <MessageCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold text-blue-600">{stats.open}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.inProgress}
                </p>
              </div>
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.resolved}
                </p>
              </div>
              <CheckCheck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Urgent</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.urgent}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="border-b border-border">
        <nav className="flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All Tickets
          </button>
          <button
            onClick={() => setActiveTab('Open')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'Open'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Open
          </button>
          <button
            onClick={() => setActiveTab('In-progress')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'In-progress'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setActiveTab('Resolved')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'Resolved'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Resolved
          </button>
        </nav>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle className="text-lg">Support Tickets</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowInlineFilters(!showInlineFilters);
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Tickets"
          />
          <div className="mt-4">
            <DataTable
              columns={columns}
              data={filteredTickets}
              searchValue={searchTerm}
              searchPlaceholder="Search tickets ID, User, Email, Subject..."
              onSearchChange={setSearchTerm}
              onSearchReset={() => setSearchTerm('')}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showAddButton={false}
              showExportButton={false}
              showColumnVisibility={true}
              initialPageSize={10}
              initialSortBy={{ id: 'createdAt', desc: true }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Support Ticket Modal */}
      <SupportTicketModal
        key={selectedTicket?.id}
        ticket={selectedTicket}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onReplySubmit={handleReplySubmit}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        loading={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Support Ticket"
        message={`Are you sure you want to delete this support ticket? This action cannot be undone.
Ticket ID: ${deleteConfirm.ticket?.ticketId}
`}
        confirmText="Delete Ticket"
        cancelText="Cancel"
        type="danger"
        loading={isDeleting}
        loadingText="Deleting..."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default AdminSupport;
